<template>
  <div class="image-text-carousel">
    <!-- Tabs -->
    <div class="image-text-carousel__tabs">
      <div
        v-for="(item, index) in infos"
        :key="item.title"
        :class="{
          'image-text-carousel__tab-item--active': activeIndex === index,
        }"
        class="image-text-carousel__tab-item"
        @click="goTo(index)"
      >
        <p class="image-text-carousel__tab-title">{{ item.title }}</p>
      </div>
    </div>

    <!-- Card Content -->
    <div class="image-text-carousel__card">
      <!-- Left: Text Content Wrapper -->
      <div class="image-text-carousel__content-wrapper">
        <div class="image-text-carousel__content">
          <h3 class="image-text-carousel__title">
            {{ infos[activeIndex].title }}
          </h3>
          <p v-if="infos[activeIndex].desc" class="image-text-carousel__desc">
            {{ infos[activeIndex].desc }}
          </p>
          <ul
            v-if="infos[activeIndex].list && infos[activeIndex]?.list?.length"
            class="image-text-carousel__list"
          >
            <li
              v-for="(listItem, idx) in infos[activeIndex].list"
              :key="idx"
              class="image-text-carousel__list-item"
            >
              <IconChecked class="image-text-carousel__list-icon" />
              <p>
                <span>{{ listItem.label }}</span>
                <span>{{ listItem.content }}</span>
              </p>
            </li>
          </ul>
          <slot name="button" />
        </div>
      </div>

      <!-- Right: Image -->
      <div class="image-text-carousel__image-wrapper">
        <Transition name="fade" mode="out-in">
          <img
            :key="activeIndex"
            class="image-text-carousel__image"
            :src="src"
            :alt="infos[activeIndex].subTitle || infos[activeIndex].title"
          />
        </Transition>
      </div>
    </div>

    <!-- Dots Navigation -->
    <div v-if="showDots" class="image-text-carousel__dots">
      <Dot
        v-for="(item, index) in infos"
        :key="item.title"
        :active="activeIndex === index"
        @click="goTo(index)"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import IconChecked from '@/design/icons/check.svg?component';
import Dot from '~/components/xDot/index.vue';

const props = withDefaults(
  defineProps<{
    infos: {
      title?: string;
      url: string;
      desc?: string;
      subTitle?: string;
      list?: {
        label: string;
        content: string;
      }[];
    }[];
    // 底部圆点导航
    showDots?: boolean;
  }>(),
  {
    showDots: false,
  },
);

const activeIndex = ref(0);
const src = ref(props.infos[activeIndex.value].url);

// 预加载所有图片
const preloadImages = () => {
  props.infos.forEach(info => {
    const img = new Image();
    img.src = info.url;
  });
};

const goTo = (index: number) => {
  activeIndex.value = index;
};

watch(
  () => activeIndex.value,
  val => {
    src.value = props.infos[val].url;
  },
);

// 组件挂载时预加载图片
onMounted(() => {
  preloadImages();
});
</script>

<style lang="scss" scoped>
.image-text-carousel {
  position: relative;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;

  .image-text-carousel__tabs {
    display: flex;
    gap: 16px;
    margin-bottom: 40px;
  }

  .image-text-carousel__tab-item {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 12px 24px;
    gap: 10px;
    position: relative;
    font-size: 14px;
    line-height: 14px;
    background-color: var(--x-back-3);
    color: var(--x-text-3);
    border-radius: 22px;
    cursor: pointer;
    overflow: hidden;

    p {
      margin: 0;
    }

    &:hover {
      background-color: var(--x-back-5);

      .image-text-carousel__tab-title {
        color: var(--x-text-3);
      }
    }

    &.image-text-carousel__tab-item--active {
      background-color: var(--x-text-1);
      color: var(--x-white-1);

      .image-text-carousel__tab-title {
        color: var(--x-white-1);
      }
    }
  }

  .image-text-carousel__tab-title {
    color: var(--x-text-3);
    text-align: center;
    font-size: 14px;
    font-style: normal;
    line-height: normal;
  }

  .image-text-carousel__card {
    display: flex;
    gap: 0;
    width: 100%;
    background-color: var(--x-back-3);
    border-radius: 12px;
    overflow: hidden;
    min-height: 530px;

    // 1440px ~ 1600px 之间从 530px 增长到 636px
    @media (min-width: 1600px) and (max-width: 1920px) {
      min-height: clamp(
        530px,
        calc(530px + (636 - 530) * ((100vw - 1600px) / (1920 - 1600))),
        636px
      );
    }

    // 1600px 以上固定为 636px
    @media (min-width: 1920px) {
      min-height: 636px;
    }
  }

  .image-text-carousel__content-wrapper {
    flex: 1;
    display: flex;
  }

  .image-text-carousel__content {
    flex: 1;
    display: flex;
    flex-direction: column;
    margin: 48px 32px;

    // 1440px ~ 1600px 之间线性增长
    @media (min-width: 1600px) and (max-width: 1920px) {
      margin: clamp(48px, calc(48px + (80 - 48) * ((100vw - 1600px) / (1920 - 1600))), 80px)
        clamp(32px, calc(32px + (48 - 32) * ((100vw - 1600px) / (1920 - 1600))), 48px);
    }

    // 1600px 以上固定为 80px
    @media (min-width: 1920px) {
      margin: 80px 48px;
    }
  }

  .image-text-carousel__button {
    position: absolute;
    bottom: 0;
    left: 0;
  }

  .image-text-carousel__title {
    color: var(--x-text-1);
    font-style: normal;
    font-weight: 400;
    line-height: normal;
    margin: 0;
    margin-bottom: 32px;
    font-size: 24px;

    // 1440px ~ 1600px 之间字号从 28px 增长到 32px
    @media (min-width: 1600px) and (max-width: 1920px) {
      font-size: clamp(24px, calc(24px + (32 - 24) * ((100vw - 1600px) / (1920 - 1600))), 32px);
    }

    // 1600px 以上固定为 32px
    @media (min-width: 1920px) {
      font-size: 32px;
      margin-bottom: 48px;
    }
  }

  .image-text-carousel__desc {
    margin: 0;
    color: var(--x-text-3);
    font-size: 16px;
    font-style: normal;
    font-weight: 400;
    line-height: 32px;
    white-space: pre-line;
    margin-bottom: 32px;
    // 1600px 以上固定为 32px
    @media (min-width: 1920px) {
      margin-bottom: 48px;
    }
  }

  .image-text-carousel__list {
    margin: 0;
    padding-left: 0;
    list-style: none;
    display: flex;
    flex-direction: column;
    gap: 16px;

    // 1600px 以上固定为 32px
    @media (min-width: 1920px) {
      gap: 32px;
    }
  }

  .image-text-carousel__list-item {
    color: var(--x-text-3);
    font-size: 16px;
    font-style: normal;
    font-weight: 400;
    line-height: 28px;
    display: flex;
    align-items: flex-start;

    p {
      margin: 0;
      span:first-of-type {
        font-weight: var(--x-weight-medium);
      }
    }
  }

  .image-text-carousel__image-wrapper {
    flex: 1;
    overflow: hidden;
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    aspect-ratio: 6 / 5.3;
  }

  .image-text-carousel__image {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  // 图片切换过渡动画
  .fade-enter-active,
  .fade-leave-active {
    transition: opacity 0.3s ease;
  }

  .fade-enter-from {
    opacity: 0;
  }

  .fade-leave-to {
    opacity: 0;
  }

  .image-text-carousel__dots {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 16px;
  }

  .image-text-carousel__list-icon {
    font-size: 18px;
    color: var(--x-purple-6);
    width: 18px;
    height: 18px;
    flex-shrink: 0;
    margin-right: 8px;
    margin-top: 5px;

    :deep(.svg-icon) {
      width: 18px;
      height: 18px;
    }
  }
}
</style>
